// frontend/src/api/index.ts - COMPREHENSIVE API CLIENT VERSION 6.0.0
// Complete API client implementation with unified response handling and latest route integration

import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import type { 
  // Authentication types
  LoginCredentials, 
  User, 
  AuthResponse, 
  PasswordResetRequest,
  PasswordChangeRequest,
  UserRegistrationRequest,
  LogoutResponse,
  RegistrationResponse, 
  RefreshTokenResponse,
  CurrentUserResponse,
  TokenVerificationResponse,
  AuthErrorResponse,
  
  // Core domain types
  Organization,
  Group,
  Project,
  Workflow,
  Correspondence,
  Response,
  Attachment,
  Notification,
  Email,
  Exclusion,
  StaticDocument,

  // V3: Correspondence split model types
  CorrespondenceEmail,
  CorrespondenceAttachment,
  CorrespondenceAI,
  CorrespondenceAudit,
  CorrespondenceWithRelated,
  CorrespondenceFull,
  CorrespondenceV3Options,
  
  // Query parameter types
  QueryParams,
  UsersQueryParams,
  OrganizationQueryParams,
  GroupsQueryParams,
  WorkflowQueryParams,
  CorrespondenceQueryParams,
  EmailQueryParams,
  AttachmentQueryParams,
  ResponseQueryParams,
  ProjectQueryParams,
  NotificationQueryParams,
  ExclusionQueryParams,
  StaticDocumentQueryParams,
  
  // Request types
  UserCreateRequest,
  UserUpdateRequest,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  GroupCreateRequest,
  GroupUpdateRequest,
  AddMembersRequest,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  CorrespondenceCreateRequest,
  CorrespondenceUpdateRequest,
  ResponseCreateRequest,
  ResponseUpdateRequest,
  AttachmentUploadRequest,
  AttachmentUploadResponse,
  AttachmentUpdateRequest,
  EmailPreservationRequest,
  EmailRetentionPolicyRequest,
  EmailCleanupRequest,
  EmailBatchOperationRequest,
  ExclusionCreateRequest,
  ExclusionUpdateRequest,
  ExclusionTestRequest,
  StaticDocumentCreateRequest,
  StaticDocumentUpdateRequest,
  BatchOperationRequest,
  
  // Response types
  StandardResponse,
  PaginatedResponse,
  ApiErrorResponse,
  OrganizationListResponse,
  OrganizationDetailResponse,
  OrganizationCreateResponse,
  UserListResponse,
  GroupListResponse,
  WorkflowListResponse,
  OAuth2AuthorizeResponse,
  EmailAccountsResponse,
  EmailLifecycleAnalyticsResponse,
  DashboardQueryParams,
  DashboardMetrics,
  DashboardSummary,
  
  // Utility types
  UploadedFile,
  CacheEntry,
  ApiClient,
  ApiRequest,
} from './types';

// ===== API CONFIGURATION =====
const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  retryAttempts: 3,
  retryDelay: 1000,
} as const;

// ===== CACHE MANAGEMENT =====
class CacheManagerImpl {
  private cache = new Map<string, CacheEntry>();
  
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }
  
  set<T>(key: string, data: T, ttl: number = 300000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
      key
    });
  }
  
  delete(key: string): void {
    this.cache.delete(key);
  }
  
  clear(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
  
  has(key: string): boolean {
    return this.cache.has(key) && !this.isExpired(key);
  }
  
  private isExpired(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    
    return Date.now() > entry.timestamp + entry.ttl;
  }
}

const cacheManager = new CacheManagerImpl();

// ===== AXIOS INSTANCE CONFIGURATION =====
class ApiClientImpl implements ApiClient {
  public instance: AxiosInstance;
  private requestQueue: Array<() => Promise<any>> = [];
  private isRefreshingToken = false;
  private isRedirectingToLogin = false; // Prevent multiple redirects

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // CRITICAL FIX: Get token from Zustand auth store instead of localStorage
        let token = null;
        
        try {
          // First try the Zustand auth store (current implementation)
          const authStoreStr = localStorage.getItem('auth-store');
          if (authStoreStr) {
            const authStore = JSON.parse(authStoreStr);
            token = authStore.state?.token;
          }
          
          // Fallback to legacy localStorage location for backward compatibility
          if (!token) {
            token = localStorage.getItem('authToken');
          }
        } catch (error) {
          console.warn('Failed to retrieve token from auth store:', error);
          // Fallback to legacy method
          token = localStorage.getItem('authToken');
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - also update token refresh to work with Zustand store
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
          await this.handleTokenRefresh(error);
        }
        return Promise.reject(error);
      }
    );
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public generatePublicRequestId(): string {
    return this.generateRequestId();
  }

  private async handleTokenRefresh(error: AxiosError): Promise<void> {
    // Prevent multiple redirects - throw error to stop request processing
    if (this.isRedirectingToLogin) {
      throw new Error('Session expired - redirecting to login');
    }

    // Get refresh token from Zustand store
    let refreshToken = null;

    try {
      const authStoreStr = localStorage.getItem('auth-store');
      if (authStoreStr) {
        const authStore = JSON.parse(authStoreStr);
        refreshToken = authStore.state?.refreshToken;
      }

      // Fallback to legacy location
      if (!refreshToken) {
        refreshToken = localStorage.getItem('refreshToken');
      }
    } catch (error) {
      console.warn('Failed to get refresh token from auth store:', error);
      refreshToken = localStorage.getItem('refreshToken');
    }

    // If no refresh token, redirect to login immediately
    if (!refreshToken) {
      console.warn('🔐 No refresh token found - session has expired');
      this.redirectToLogin('Your session has expired. Please log in again.');
      throw new Error('Session expired - no refresh token');
    }

    // If already refreshing, wait for it to complete by queuing this request
    if (this.isRefreshingToken) {
      // Don't return - let the request fail and user will see the redirect
      throw new Error('Token refresh in progress');
    }

    this.isRefreshingToken = true;

    try {
      const response = await axios.post(`${API_CONFIG.baseURL}/auth/refresh`, {
        refreshToken
      });

      // CRITICAL FIX: Handle new response format correctly
      const { accessToken, expiresIn } = response.data;

      if (accessToken) {
        // Update both locations for compatibility
        localStorage.setItem('authToken', accessToken);

        // CRITICAL FIX: Also update the Zustand store if it exists
        try {
          const authStoreStr = localStorage.getItem('auth-store');
          if (authStoreStr) {
            const authStore = JSON.parse(authStoreStr);
            if (authStore.state) {
              authStore.state.token = accessToken;
              localStorage.setItem('auth-store', JSON.stringify(authStore));
            }
          }
        } catch (error) {
          console.warn('Failed to update token in auth store:', error);
        }

        // Note: Backend may not return new refresh token, that's OK
      } else {
        throw new Error('No access token in refresh response');
      }

      // Retry failed requests
      await this.retryQueuedRequests();

    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      this.redirectToLogin('Your session has expired. Please log in again.');
    } finally {
      this.isRefreshingToken = false;
      this.requestQueue = [];
    }
  }

  private async retryQueuedRequests(): Promise<void> {
    const requests = [...this.requestQueue];
    this.requestQueue = [];
    
    await Promise.allSettled(requests.map(request => request()));
  }

  private redirectToLogin(message?: string): void {
    // Prevent multiple redirects
    if (this.isRedirectingToLogin) {
      return;
    }
    this.isRedirectingToLogin = true;

    console.warn('🔐 Session expired - redirecting to login page');

    // Show user-friendly message
    const userMessage = message || 'Your session has expired. Please log in again.';

    // Try to display a user-friendly notification if possible
    try {
      // Check if we have a toast notification system available
      const event = new CustomEvent('session-expired', {
        detail: { message: userMessage }
      });
      window.dispatchEvent(event);
    } catch (error) {
      // If custom event fails, just log
    }

    // Clear legacy locations
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // CRITICAL FIX: Also clear Zustand auth store
    try {
      const authStoreStr = localStorage.getItem('auth-store');
      if (authStoreStr) {
        const authStore = JSON.parse(authStoreStr);
        if (authStore.state) {
          authStore.state.token = null;
          authStore.state.refreshToken = null;
          authStore.state.user = null;
          authStore.state.isAuthenticated = false;
          localStorage.setItem('auth-store', JSON.stringify(authStore));
        }
      }
    } catch (error) {
      console.warn('Failed to clear auth store on logout:', error);
      // If auth store is corrupted, remove it entirely
      localStorage.removeItem('auth-store');
    }

    if (typeof window !== 'undefined') {
      // Store the message for the login page to display
      sessionStorage.setItem('login-message', userMessage);

      // Immediate redirect - no setTimeout delay
      // This prevents the UI from being in a broken state
      window.location.href = '/login';
    }
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: ApiRequest): Promise<T> {
    try {
      // Check cache first
      const cacheKey = this.generateCacheKey('GET', url, config?.params);
      const cached = cacheManager.get<T>(cacheKey);
      if (cached && !config?.headers?.['Cache-Control']?.includes('no-cache')) {
        return cached;
      }

      const response = await this.instance.get(url, {
        ...config,
        params: config?.params,
        timeout: config?.timeout || API_CONFIG.timeout
      });
      
      const result = this.handleResponse<T>(response);
      
      // Cache successful GET requests
      if (response.status === 200 && !url.includes('stream') && !url.includes('download')) {
        cacheManager.set(cacheKey, result, 300000); // 5 minutes default
      }
      
      return result;
    } catch (error) {
      this.handleError(error as AxiosError<ApiErrorResponse>);
      throw error; // Re-throw for caller to handle
    }
  }

  async post<T = any>(url: string, data?: any, config?: ApiRequest): Promise<T> {
    try {
      // Clear related cache
      this.clearRelatedCache(url);
      
      const response = await this.instance.post(url, data, {
        ...config,
        timeout: config?.timeout || API_CONFIG.timeout
      });
      
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as AxiosError<ApiErrorResponse>);
      throw error;
    }
  }

  async put<T = any>(url: string, data?: any, config?: ApiRequest): Promise<T> {
    try {
      this.clearRelatedCache(url);
      
      const response = await this.instance.put(url, data, {
        ...config,
        timeout: config?.timeout || API_CONFIG.timeout
      });
      
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as AxiosError<ApiErrorResponse>);
      throw error;
    }
  }

  async patch<T = any>(url: string, data?: any, config?: ApiRequest): Promise<T> {
    try {
      this.clearRelatedCache(url);
      
      const response = await this.instance.patch(url, data, {
        ...config,
        timeout: config?.timeout || API_CONFIG.timeout
      });
      
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as AxiosError<ApiErrorResponse>);
      throw error;
    }
  }

  async delete<T = any>(url: string, config?: ApiRequest): Promise<T> {
    try {
      this.clearRelatedCache(url);
      
      const response = await this.instance.delete(url, {
        ...config,
        timeout: config?.timeout || API_CONFIG.timeout
      });
      
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as AxiosError<ApiErrorResponse>);
      throw error;
    }
  }

  // Utility methods
  public handleResponse<T>(response: AxiosResponse): T {
    const body = response.data;

    // Handle { success: false } errors
    if (body?.success === false) {
      throw new Error(body.error || 'Request failed');
    }

    // Handle { success: true, data: { <entity>: [...], pagination } }
    // e.g. emails endpoint: { success, data: { emails, pagination, filters, summary } }
    if (body?.success === true && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      const inner = body.data;

      // If inner.data exists and pagination is at this level, it's already normalized
      if (inner.data !== undefined && inner.pagination !== undefined) {
        return body as T;
      }

      // Detect entity array inside data: { emails: [...], pagination }
      // Find the array field that is the entity list
      const arrayKey = Object.keys(inner).find(
        k => k !== 'pagination' && k !== 'filters' && k !== 'summary' && k !== 'metadata' && Array.isArray(inner[k])
      );

      if (arrayKey && inner.pagination) {
        return { data: inner[arrayKey], pagination: inner.pagination, ...body.metadata && { metadata: body.metadata } } as T;
      }

      // If data field has pagination at top level of body
      if (body.pagination) {
        return body as T;
      }

      // Non-paginated success response: extract data
      return inner as T;
    }

    // Handle non-success-wrapped paginated responses:
    // e.g. { correspondences: [...], pagination } or { responses: [...], pagination }
    if (body?.pagination && typeof body === 'object') {
      const arrayKey = Object.keys(body).find(
        k => k !== 'pagination' && k !== 'filters' && k !== 'summary' && k !== 'metadata' && Array.isArray(body[k])
      );

      if (arrayKey) {
        return { data: body[arrayKey], pagination: body.pagination, ...(body.filters && { filters: body.filters }), ...(body.summary && { summary: body.summary }), ...(body.metadata && { metadata: body.metadata }) } as T;
      }
    }

    // Handle { success: true } with top-level data+pagination
    if (body?.success === true && body.pagination) {
      return body as T;
    }

    // Default: return body as-is
    return body;
  }

  private handleError(error: AxiosError<ApiErrorResponse>): void {
    const status = error.response?.status;
    // Some backend routes (e.g. /api/auth/*) return errors as
    // { error: { type, message, status, correlationId, timestamp, details } }
    // instead of a plain string. Coerce to a string here so downstream catch
    // handlers and React toasts never accidentally render an object child.
    const rawError = error.response?.data?.error;
    const rawMessage = error.response?.data?.message;
    const toMessage = (v: any): string => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'object') {
        // Common nested shapes: {message, type, ...}
        if (typeof v.message === 'string') return v.message;
        try { return JSON.stringify(v); } catch { return String(v); }
      }
      return String(v);
    };
    // A validation failure names the field that is wrong. Showing the route's
    // generic summary instead ("Validation failed") tells the user nothing —
    // which is exactly what a workflow save did on 2026-09-19.
    const fieldErrors = (error.response?.data as any)?.validationErrors
      || (error.response?.data as any)?.errors;
    const fieldMessage = Array.isArray(fieldErrors)
      ? fieldErrors.map((e: any) => (typeof e === 'string' ? e : e?.message || e?.msg)).filter(Boolean).join('; ')
      : '';

    const originalMessage = fieldMessage || toMessage(rawError) || toMessage(rawMessage) || error.message;

    // Enhance error messages by status code so the UI never falls back to
    // the raw axios string ("Request failed with status code 4xx").
    let enhancedMessage = originalMessage;
    if (status === 401) {
      const isLoginAttempt = error.config?.url?.includes('/auth/login');
      if (!isLoginAttempt) {
        enhancedMessage = 'Your session has expired. Redirecting to login...';
      }
    } else if (status === 402) {
      // Payment Required — backend uses this for expired trial / lapsed
      // subscription. Prefer the backend's own message; only fall back if
      // missing.
      enhancedMessage = originalMessage && !originalMessage.includes('status code')
        ? originalMessage
        : 'Your subscription has expired. Please contact your administrator to upgrade your plan.';
    } else if (status === 403 && !originalMessage) {
      enhancedMessage = 'You do not have permission to perform this action.';
    } else if (status && status >= 500 && (!originalMessage || originalMessage.includes('status code'))) {
      enhancedMessage = 'A server error occurred. Please try again or contact support.';
    } else if (originalMessage && originalMessage.includes('status code')) {
      // Axios default "Request failed with status code N" — drop it for
      // something terser if the backend gave us nothing better.
      enhancedMessage = status ? `Request failed (HTTP ${status}).` : 'Request failed.';
    }

    // Propagate the enhanced message to the error object so axios's
    // `error.message` (used by toasts and useApi.ts) becomes user-friendly
    // instead of the raw "Request failed with status code N" default.
    try {
      Object.defineProperty(error, 'message', { value: enhancedMessage, writable: true, configurable: true });
    } catch { /* axios error message is writable by default; ignore */ }
    if (error.response?.data) {
      error.response.data.error = enhancedMessage;
      error.response.data.message = enhancedMessage;
    }

    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: status,
      message: enhancedMessage,
      requestId: error.config?.headers?.['X-Request-ID']
    });

    // Log to external error tracking service if configured
    if (import.meta.env.VITE_ERROR_TRACKING_ENABLED === 'true') {
      this.logToErrorTracking(error);
    }
  }

  private logToErrorTracking(error: AxiosError<ApiErrorResponse>): void {
    // Integrate with services like Sentry, LogRocket, etc.
    // Implementation depends on chosen service
    console.warn('Error tracking not implemented');
  }

  private generateCacheKey(method: string, url: string, params?: any): string {
    return generateSafeCacheKey(method, url, params);
  }

  private clearRelatedCache(url: string): void {
    const segments = url.split('/');
    const resource = segments[1]; // e.g., 'organizations', 'users', etc.
    
    cacheManager.clear(resource);
  }
}

const generateSafeCacheKey = (method: string, url: string, params?: any): string => {
  try {
    const paramStr = params ? JSON.stringify(params, (key, value) => {
      // Handle circular references and functions
      if (typeof value === 'function') return '[Function]';
      if (typeof value === 'object' && value !== null) {
        if (value.constructor !== Object && value.constructor !== Array) {
          return '[Object]';
        }
      }
      return value;
    }) : '';
    return `${method}:${url}:${btoa(paramStr)}`;
  } catch (error) {
    console.warn('Failed to generate cache key, using fallback:', error);
    return `${method}:${url}:${Date.now()}`;
  }
};

// Create singleton instance
const apiClient = new ApiClientImpl();

// ===== AUTHENTICATION API =====
export const auth = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
      
      // CRITICAL FIX: Handle both accessToken and token fields for compatibility
      const token = response.accessToken || response.token;
      if (!token) {
        throw new Error('No authentication token received from server');
      }

      // Store token and user data
      localStorage.setItem('authToken', token);
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      
      // Ensure user data exists before storing
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      
      // Emit auth state change event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('authStateChanged'));
      }
      
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },

  logout: async (): Promise<LogoutResponse> => {
    try {
      const response = await apiClient.post<LogoutResponse>('/auth/logout');
      return response;
    } catch (error) {
      console.warn('Logout request failed:', error);
      // CRITICAL FIX: Always provide valid LogoutResponse even on failure
      return {
        message: 'Logout completed (server request failed)',
        timestamp: new Date().toISOString()
      };
    } finally {
      // CRITICAL FIX: Always clear storage regardless of server response
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      cacheManager.clear();
      
      // Emit auth state change event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('authStateChanged'));
      }
    }
  },

  register: async (userData: UserRegistrationRequest): Promise<RegistrationResponse> => {
    try {
      return await apiClient.post<RegistrationResponse>('/auth/register', userData);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  },

  refreshToken: async (): Promise<RefreshTokenResponse> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    try {
      const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', { refreshToken });
      
      // CRITICAL FIX: Update stored access token with correct field name
      if (response.accessToken) {
        localStorage.setItem('authToken', response.accessToken);
        
        // Emit auth state change event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('authStateChanged'));
        }
      } else {
        throw new Error('No access token received in refresh response');
      }
      
      return response;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear tokens on refresh failure
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  },

  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    try {
      return await apiClient.get<CurrentUserResponse>('/auth/me');
    } catch (error) {
      console.error('Get current user failed:', error);
      throw error;
    }
  },

  verifyToken: async (token?: string): Promise<TokenVerificationResponse> => {
    try {
      // CRITICAL FIX: Handle optional token parameter correctly
      const requestBody: { token?: string } = {};
      if (token) {
        requestBody.token = token;
      }
      // If no token provided, server will extract from Authorization header

      return await apiClient.post<TokenVerificationResponse>('/auth/verify', requestBody);
    } catch (error) {
      console.error('Token verification failed:', error);
      throw error;
    }
  },

  // FIXED: Email account management - using correct auth endpoints
  getEmailAccounts: async (userId?: string): Promise<EmailAccountsResponse> => {
    // Backend route: GET /auth/email-accounts/:userId? (userId is optional)
    const url = userId ? `/auth/email-accounts/${userId}` : '/auth/email-accounts';
    return apiClient.get<EmailAccountsResponse>(url);
  },

  removeEmailAccount: async (userId: string, email: string): Promise<StandardResponse<void>> => {
    // Backend route: DELETE /auth/email-accounts/:userId/:email
    return apiClient.delete<StandardResponse<void>>(`/auth/email-accounts/${userId}/${email}`);
  },

  toggleEmailAccountStatus: async (userId: string, email: string, isActive: boolean): Promise<StandardResponse<any>> => {
    // Backend route: PUT /auth/email-accounts/:userId/:email/toggle
    return apiClient.put<StandardResponse<any>>(`/auth/email-accounts/${userId}/${email}/toggle`, { isActive });
  },

  oauth2Authorize: async (provider?: string, forceConsent?: boolean): Promise<OAuth2AuthorizeResponse> => {
    const params = new URLSearchParams();
    if (provider) params.set('provider', provider);
    if (forceConsent) params.set('forceConsent', 'true');
    const queryString = params.toString();
    return apiClient.get<OAuth2AuthorizeResponse>(`/auth/oauth2/authorize${queryString ? `?${queryString}` : ''}`);
  },

  // Enterprise (org-managed) mailbox enrollment — used when the org has
  // Microsoft 365 in Application Permissions mode. The user picks/types their
  // own mailbox; no per-user OAuth.
  getEnterpriseAvailable: async (): Promise<any> =>
    apiClient.get<any>('/auth/email-accounts/enterprise/available'),

  addEnterpriseAccount: async (data: { email: string; providerType: 'microsoft365' | 'google_workspace' }): Promise<any> =>
    apiClient.post<any>('/auth/email-accounts/enterprise', data)
};

// ===== ORGANIZATIONS API =====
export const organizations = {
  list: async (params?: OrganizationQueryParams): Promise<OrganizationListResponse> => {
    return apiClient.get<OrganizationListResponse>('/organizations', { params });
  },

  // FIXED: Use 'id' parameter to match backend route /organizations/:id
  get: async (id: string): Promise<OrganizationDetailResponse> => {
    return apiClient.get<OrganizationDetailResponse>(`/organizations/${id}`);
  },

  create: async (orgData: OrganizationCreateRequest): Promise<OrganizationCreateResponse> => {
    return apiClient.post<OrganizationCreateResponse>('/organizations', orgData);
  },

  // FIXED: Use 'id' parameter to match backend
  update: async (id: string, orgData: OrganizationUpdateRequest): Promise<StandardResponse<Organization>> => {
    return apiClient.put<StandardResponse<Organization>>(`/organizations/${id}`, orgData);
  },

  // FIXED: Use 'id' parameter to match backend
  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/organizations/${id}`);
  },

  // Activate organization
  activate: async (id: string): Promise<StandardResponse<Organization>> => {
    return apiClient.post<StandardResponse<Organization>>(`/organizations/${id}/activate`, {});
  },

  // Deactivate organization
  deactivate: async (id: string): Promise<StandardResponse<Organization>> => {
    return apiClient.post<StandardResponse<Organization>>(`/organizations/${id}/deactivate`, {});
  },

  // FIXED: Use 'id' parameter to match backend /organizations/:id/usage
  getUsage: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${id}/usage`);
  },

  // FIXED: Use 'id' parameter to match backend /organizations/:id/groups  
  getGroups: async (id: string, params?: GroupsQueryParams): Promise<GroupListResponse> => {
    return apiClient.get<GroupListResponse>(`/organizations/${id}/groups`, { params });
  },

  // Implementor-specific routes (kept existing functionality)
  getImplementorData: async (params?: QueryParams): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/organizations/implementor-data', { params });
  },

  // Organization user management (kept existing)
  getUsers: async (id: string, params?: UsersQueryParams): Promise<UserListResponse> => {
    return apiClient.get<UserListResponse>(`/organizations/${id}/users`, { params });
  },

  addUser: async (id: string, userData: UserCreateRequest): Promise<StandardResponse<User>> => {
    return apiClient.post<StandardResponse<User>>(`/organizations/${id}/users`, userData);
  },

  removeUser: async (id: string, userId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/organizations/${id}/users/${userId}`);
  },

  // Organization project management (kept existing)
  getProjects: async (id: string, params?: ProjectQueryParams): Promise<PaginatedResponse<Project>> => {
    return apiClient.get<PaginatedResponse<Project>>(`/organizations/${id}/projects`, { params });
  },

  // Organization workflow management (kept existing)
  getWorkflows: async (id: string, params?: WorkflowQueryParams): Promise<WorkflowListResponse> => {
    return apiClient.get<WorkflowListResponse>(`/organizations/${id}/workflows`, { params });
  },

  // Organization email management (kept existing)
  getEmails: async (id: string, params?: EmailQueryParams): Promise<PaginatedResponse<Email>> => {
    return apiClient.get<PaginatedResponse<Email>>(`/organizations/${id}/emails`, { params });
  },

  // Organization correspondence management (kept existing)
  getCorrespondences: async (id: string, params?: CorrespondenceQueryParams): Promise<PaginatedResponse<Correspondence>> => {
    return apiClient.get<PaginatedResponse<Correspondence>>(`/organizations/${id}/correspondences`, { params });
  },

  // Organization analytics and metrics (kept existing)
  getAnalytics: async (id: string, params?: QueryParams): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${id}/analytics`, { params });
  },

  getMetrics: async (id: string, params?: QueryParams): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${id}/metrics`, { params });
  },

  // Organization settings and configuration (kept existing)
  getSettings: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${id}/settings`);
  },

  updateSettings: async (id: string, settings: any): Promise<StandardResponse<any>> => {
    return apiClient.put<StandardResponse<any>>(`/organizations/${id}/settings`, settings);
  },

  // Bulk operations (kept existing)
  bulkUpdate: async (updateData: BatchOperationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/organizations/bulk-update', updateData);
  },

  bulkDelete: async (deleteData: BatchOperationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/organizations/bulk-delete', deleteData);
  },

  // Search and filtering (kept existing)
  search: async (query: string, filters?: any): Promise<PaginatedResponse<Organization>> => {
    return apiClient.get<PaginatedResponse<Organization>>('/organizations/search', {
      params: { q: query, ...filters }
    });
  },

  updateRetentionPolicy: async (orgId: string, data: any): Promise<StandardResponse<any>> => {
    return apiClient.put<StandardResponse<any>>(`/organizations/${orgId}/retention-policy`, data);
  },

  cleanupEmails: async (orgId: string, data?: any): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/organizations/${orgId}/cleanup-expired`, data || {});
  },

  getAIUsage: async (orgId: string, startDate?: string, endDate?: string): Promise<StandardResponse<any>> => {
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const query = qs.toString();
    return apiClient.get<StandardResponse<any>>(`/organizations/${orgId}/ai-usage${query ? `?${query}` : ''}`);
  },

  getAIUsageDaily: async (orgId: string, startDate?: string, endDate?: string): Promise<StandardResponse<any>> => {
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const query = qs.toString();
    return apiClient.get<StandardResponse<any>>(`/organizations/${orgId}/ai-usage/daily${query ? `?${query}` : ''}`);
  },

  getTrialStatus: async (orgId: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${orgId}/trial-status`);
  }
};

// ===== USERS API =====
export const users = {
  list: async (params?: UsersQueryParams): Promise<UserListResponse> => {
    return apiClient.get<UserListResponse>('/users', { params });
  },

  get: async (id: string): Promise<StandardResponse<User>> => {
    return apiClient.get<StandardResponse<User>>(`/users/${id}`);
  },

  create: async (userData: UserCreateRequest): Promise<StandardResponse<User>> => {
    return apiClient.post<StandardResponse<User>>('/users', userData);
  },

  update: async (id: string, userData: UserUpdateRequest): Promise<StandardResponse<User>> => {
    return apiClient.put<StandardResponse<User>>(`/users/${id}`, userData);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/users/${id}`);
  },

  activate: async (id: string): Promise<StandardResponse<User>> => {
    return apiClient.post<StandardResponse<User>>(`/users/${id}/activate`, {});
  },

  deactivate: async (id: string): Promise<StandardResponse<User>> => {
    return apiClient.post<StandardResponse<User>>(`/users/${id}/deactivate`, {});
  },

  resetPassword: async (id: string, data?: { newPassword?: string; generatePassword?: boolean }): Promise<StandardResponse<{ temporaryPassword: string }>> => {
    return apiClient.put<StandardResponse<{ temporaryPassword: string }>>(`/users/${id}/reset-password`, data || { generatePassword: true });
  },

  // User statistics
  getStats: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/users/${id}/stats`);
  },

  getActivity: async (id: string, params?: QueryParams): Promise<PaginatedResponse<any>> => {
    return apiClient.get<PaginatedResponse<any>>(`/users/${id}/activity`, { params });
  }
};

// ===== GROUPS API =====
export const groups = {
  // FIXED: Cross-organization groups - use correct backend route
  listCrossOrg: async (params?: GroupsQueryParams): Promise<GroupListResponse> => {
    return apiClient.get<GroupListResponse>('/groups', { params }); // Changed from '/groups/cross-organization'
  },

  // FIXED: Organization-specific groups - maintained all existing functionality
  list: async (organizationId: string, params?: GroupsQueryParams): Promise<GroupListResponse> => {
    return apiClient.get<GroupListResponse>(`/organizations/${organizationId}/groups`, { params });
  },

  get: async (organizationId: string, groupId: string): Promise<StandardResponse<Group>> => {
    return apiClient.get<StandardResponse<Group>>(`/organizations/${organizationId}/groups/${groupId}`);
  },

  create: async (organizationId: string, groupData: GroupCreateRequest): Promise<StandardResponse<Group>> => {
    return apiClient.post<StandardResponse<Group>>(`/organizations/${organizationId}/groups`, groupData);
  },

  update: async (organizationId: string, groupId: string, groupData: GroupUpdateRequest): Promise<StandardResponse<Group>> => {
    return apiClient.put<StandardResponse<Group>>(`/organizations/${organizationId}/groups/${groupId}`, groupData);
  },

  delete: async (organizationId: string, groupId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/organizations/${organizationId}/groups/${groupId}`);
  },

  // Group member management
  addMembers: async (organizationId: string, groupId: string, memberData: AddMembersRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/organizations/${organizationId}/groups/${groupId}/members`, memberData);
  },

  removeMember: async (organizationId: string, groupId: string, userId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/organizations/${organizationId}/groups/${groupId}/members/${userId}`);
  },

  // Leave group (for contributors to leave groups themselves)
  leaveGroup: async (organizationId: string, groupId: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/organizations/${organizationId}/groups/${groupId}/leave`);
  },

  // Group analytics and stats
  getStats: async (organizationId: string, groupId: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/organizations/${organizationId}/groups/${groupId}/stats`);
  },

  getMembers: async (organizationId: string, groupId: string, params?: QueryParams): Promise<PaginatedResponse<User>> => {
    return apiClient.get<PaginatedResponse<User>>(`/organizations/${organizationId}/groups/${groupId}/members`, { params });
  }
};

// ===== WORKFLOWS API =====
export const workflows = {
  list: async (params?: WorkflowQueryParams): Promise<WorkflowListResponse> => {
    return apiClient.get<WorkflowListResponse>('/workflows', { params });
  },

  // Organization-specific workflows - FIXED: use 'id' parameter
  listByOrganization: async (organizationId: string, params?: WorkflowQueryParams): Promise<WorkflowListResponse> => {
    return apiClient.get<WorkflowListResponse>(`/organizations/${organizationId}/workflows`, { params });
  },

  get: async (workflowId: string): Promise<StandardResponse<Workflow>> => {
    return apiClient.get<StandardResponse<Workflow>>(`/workflows/${workflowId}`);
  },

  create: async (workflowData: WorkflowCreateRequest): Promise<StandardResponse<Workflow>> => {
    return apiClient.post<StandardResponse<Workflow>>('/workflows', workflowData);
  },

  update: async (workflowId: string, workflowData: WorkflowUpdateRequest): Promise<StandardResponse<Workflow>> => {
    return apiClient.put<StandardResponse<Workflow>>(`/workflows/${workflowId}`, workflowData);
  },

  delete: async (workflowId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/workflows/${workflowId}`);
  },

  // Workflow execution (kept existing)
  execute: async (workflowId: string, data: any): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/workflows/${workflowId}/execute`, data);
  },

  getExecution: async (workflowId: string, executionId: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/workflows/${workflowId}/executions/${executionId}`);
  },

  listExecutions: async (workflowId: string, params?: QueryParams): Promise<PaginatedResponse<any>> => {
    return apiClient.get<PaginatedResponse<any>>(`/workflows/${workflowId}/executions`, { params });
  },

  // Workflow stages (kept existing)
  getStages: async (workflowId: string): Promise<StandardResponse<any[]>> => {
    return apiClient.get<StandardResponse<any[]>>(`/workflows/${workflowId}/stages`);
  },

  updateStage: async (workflowId: string, stageId: string, stageData: any): Promise<StandardResponse<any>> => {
    return apiClient.put<StandardResponse<any>>(`/workflows/${workflowId}/stages/${stageId}`, stageData);
  },

  // Workflow templates (kept existing)
  listTemplates: async (params?: QueryParams): Promise<PaginatedResponse<Workflow>> => {
    return apiClient.get<PaginatedResponse<Workflow>>('/workflows/templates', { params });
  },

  createFromTemplate: async (templateId: string, data: any): Promise<StandardResponse<Workflow>> => {
    return apiClient.post<StandardResponse<Workflow>>(`/workflows/templates/${templateId}/create`, data);
  },

  // Workflow analytics (kept existing)
  getStats: async (workflowId: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/workflows/${workflowId}/stats`);
  },

  getAnalytics: async (params?: QueryParams): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/workflows/analytics', { params });
  },

  // Workflow search (kept existing)
  search: async (query: string, filters?: any): Promise<PaginatedResponse<Workflow>> => {
    return apiClient.get<PaginatedResponse<Workflow>>('/workflows/search', {
      params: { q: query, ...filters }
    });
  }
};

// ===== PROJECTS API =====
export const projects = {
  list: async (params?: ProjectQueryParams): Promise<PaginatedResponse<Project>> => {
    return apiClient.get<PaginatedResponse<Project>>('/projects', { params });
  },

  // Unpaginated lightweight list (id + name + isActive) for filter dropdowns.
  // Avoids the paginated `list` default limit that hides projects from filters.
  listAll: async (): Promise<{ projects: Project[]; total: number }> => {
    return apiClient.get<{ projects: Project[]; total: number }>('/projects/filter-options');
  },

  get: async (id: string): Promise<StandardResponse<Project>> => {
    return apiClient.get<StandardResponse<Project>>(`/projects/${id}`);
  },

  create: async (projectData: ProjectCreateRequest): Promise<StandardResponse<Project>> => {
    return apiClient.post<StandardResponse<Project>>('/projects', projectData);
  },

  update: async (id: string, projectData: ProjectUpdateRequest): Promise<StandardResponse<Project>> => {
    return apiClient.put<StandardResponse<Project>>(`/projects/${id}`, projectData);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/projects/${id}`);
  },

  archive: async (id: string): Promise<StandardResponse<Project>> => {
    return apiClient.post<StandardResponse<Project>>(`/projects/${id}/archive`, {});
  },

  restore: async (id: string): Promise<StandardResponse<Project>> => {
    return apiClient.post<StandardResponse<Project>>(`/projects/${id}/restore`, {});
  },

  // Project correspondences
  getCorrespondences: async (id: string, params?: CorrespondenceQueryParams): Promise<PaginatedResponse<Correspondence>> => {
    return apiClient.get<PaginatedResponse<Correspondence>>(`/projects/${id}/correspondences`, { params });
  },

  // Project statistics
  getStats: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/projects/${id}/stats`);
  },

  getActivity: async (id: string, params?: QueryParams): Promise<PaginatedResponse<any>> => {
    return apiClient.get<PaginatedResponse<any>>(`/projects/${id}/activity`, { params });
  }
};

// ===== CORRESPONDENCES API =====
export const correspondences = {
  list: async (params?: CorrespondenceQueryParams): Promise<PaginatedResponse<Correspondence>> => {
    return apiClient.get<PaginatedResponse<Correspondence>>('/correspondences', { params });
  },

  get: async (id: string, params?: any): Promise<StandardResponse<Correspondence>> => {
    return apiClient.get<StandardResponse<Correspondence>>(`/correspondences/${id}`, { params });
  },

  create: async (correspondenceData: CorrespondenceCreateRequest): Promise<StandardResponse<Correspondence>> => {
    return apiClient.post<StandardResponse<Correspondence>>('/correspondences', correspondenceData);
  },

  update: async (id: string, correspondenceData: CorrespondenceUpdateRequest): Promise<StandardResponse<Correspondence>> => {
    return apiClient.put<StandardResponse<Correspondence>>(`/correspondences/${id}`, correspondenceData);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/correspondences/${id}`);
  },

  archive: async (id: string): Promise<StandardResponse<Correspondence>> => {
    return apiClient.post<StandardResponse<Correspondence>>(`/correspondences/${id}/archive`, {});
  },

  // Correspondence classification
  classify: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/correspondences/${id}/classify`, {});
  },

  reclassify: async (id: string, projectId: string): Promise<StandardResponse<Correspondence>> => {
    return apiClient.post<StandardResponse<Correspondence>>(`/correspondences/${id}/reclassify`, { projectId });
  },

  // Set which attachment represents the primary source document (e.g., cover letter).
  // Pass null to revert to the email body as the source.
  setPrimaryAttachment: async (id: string, primaryAttachmentId: string | null): Promise<StandardResponse<{ _id: string; primaryAttachmentId: string | null; needsPrimaryAttachmentSelection: boolean }>> => {
    return apiClient.patch<StandardResponse<{ _id: string; primaryAttachmentId: string | null; needsPrimaryAttachmentSelection: boolean }>>(`/correspondences/${id}/primary-attachment`, { primaryAttachmentId });
  },

  // Correspondence responses
  getResponses: async (id: string, params?: ResponseQueryParams): Promise<PaginatedResponse<Response>> => {
    return apiClient.get<PaginatedResponse<Response>>(`/correspondences/${id}/responses`, { params });
  },

  createResponse: async (id: string, responseData: ResponseCreateRequest): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/correspondences/${id}/responses`, responseData);
  },

  // Batch operations
  batchClassify: async (ids: string[], projectId: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/correspondences/batch/classify', { ids, projectId });
  },

  batchDelete: async (ids: string[]): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/correspondences/batch/delete', { ids });
  },

  // Search and analytics
  search: async (query: string, filters?: any): Promise<PaginatedResponse<Correspondence>> => {
    return apiClient.get<PaginatedResponse<Correspondence>>('/correspondences/search', {
      params: { q: query, ...filters }
    });
  },

  getAnalytics: async (params?: any): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/correspondences/analytics', { params });
  },

  // ===== V3: NEW ENDPOINTS FOR SPLIT MODEL STRUCTURE =====

  /**
   * V3: Get correspondence with optional lazy-loaded related data
   * @param id - Correspondence ID
   * @param options - Options for which related data to include
   */
  getV3: async (id: string, options?: CorrespondenceV3Options): Promise<StandardResponse<CorrespondenceWithRelated>> => {
    return apiClient.get<StandardResponse<CorrespondenceWithRelated>>(`/correspondences/${id}`, {
      params: options as any
    });
  },

  /**
   * V3: Get correspondences list with optional bulk-loaded related data
   * @param params - Query parameters including V3 options
   */
  listV3: async (params?: CorrespondenceQueryParams & CorrespondenceV3Options): Promise<PaginatedResponse<CorrespondenceWithRelated>> => {
    return apiClient.get<PaginatedResponse<CorrespondenceWithRelated>>('/correspondences', { params });
  },

  /**
   * V3: Get email content for a correspondence
   * @param id - Correspondence ID
   */
  getEmail: async (id: string): Promise<StandardResponse<CorrespondenceEmail>> => {
    return apiClient.get<StandardResponse<CorrespondenceEmail>>(`/correspondences/${id}/email`);
  },

  /**
   * V3: Get attachments for a correspondence
   * @param id - Correspondence ID
   */
  getAttachments: async (id: string): Promise<StandardResponse<CorrespondenceAttachment[]>> => {
    return apiClient.get<StandardResponse<CorrespondenceAttachment[]>>(`/correspondences/${id}/attachments`);
  },

  /**
   * V3: Get AI summary for a correspondence
   * @param id - Correspondence ID
   */
  getAISummary: async (id: string): Promise<StandardResponse<CorrespondenceAI>> => {
    return apiClient.get<StandardResponse<CorrespondenceAI>>(`/correspondences/${id}/ai-summary`);
  },

  /**
   * V3: Get audit trail for a correspondence
   * @param id - Correspondence ID
   * @param options - Pagination options
   */
  getAuditTrail: async (id: string, options?: { page?: number; limit?: number }): Promise<PaginatedResponse<CorrespondenceAudit>> => {
    return apiClient.get<PaginatedResponse<CorrespondenceAudit>>(`/correspondences/${id}/audit`, {
      params: options
    });
  },

  /**
   * V3: Get correspondence with all related data in one call
   * @param id - Correspondence ID
   */
  getFull: async (id: string): Promise<StandardResponse<CorrespondenceFull>> => {
    return apiClient.get<StandardResponse<CorrespondenceFull>>(`/correspondences/${id}/full`);
  },

  // Export job APIs
  createExportJob: async (data: {
    format: 'xlsx' | 'pdf';
    startDate: string;
    endDate: string;
    direction?: string;
    correspondenceStatus?: string;
    projectId?: string;
    search?: string;
    includeAttachmentLinks?: boolean;
    [key: string]: any;
  }): Promise<any> => {
    return apiClient.post('/correspondences/export-job', data);
  },

  getExportJobStatus: async (jobId: string): Promise<any> => {
    return apiClient.get(`/correspondences/export-job/${jobId}`);
  },

  downloadExport: async (jobId: string): Promise<Blob> => {
    const response = await apiClient.instance.get(`/correspondences/export-job/${jobId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// ===== RESPONSES API =====
export const responses = {
  list: async (params?: ResponseQueryParams): Promise<PaginatedResponse<Response>> => {
    return apiClient.get<PaginatedResponse<Response>>('/responses', { params });
  },

  get: async (id: string): Promise<StandardResponse<Response>> => {
    return apiClient.get<StandardResponse<Response>>(`/responses/${id}`);
  },

  create: async (responseData: ResponseCreateRequest): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>('/responses', responseData);
  },

  update: async (id: string, responseData: ResponseUpdateRequest): Promise<StandardResponse<Response>> => {
    return apiClient.put<StandardResponse<Response>>(`/responses/${id}`, responseData);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/responses/${id}`);
  },

  // Workflow actions - use PUT /responses/:id with action field
  approve: async (id: string, comment?: string, stageComplete?: boolean): Promise<StandardResponse<Response>> => {
    return apiClient.put<StandardResponse<Response>>(`/responses/${id}`, { action: 'Approved', comment, stageComplete });
  },

  reject: async (id: string, comment: string, returnToStage?: string): Promise<StandardResponse<Response>> => {
    return apiClient.put<StandardResponse<Response>>(`/responses/${id}`, { action: 'Rejected', comment, returnToStage });
  },

  send: async (id: string): Promise<StandardResponse<Response>> => {
    return apiClient.put<StandardResponse<Response>>(`/responses/${id}`, { action: 'Sent' });
  },

  recall: async (id: string, reason: string): Promise<StandardResponse<Response>> => {
    return apiClient.put<StandardResponse<Response>>(`/responses/${id}`, { action: 'Recalled', comment: reason });
  },

  schedule: async (id: string, scheduledFor: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/schedule`, { scheduledFor });
  },

  // Response comments
  getComments: async (id: string): Promise<StandardResponse<any[]>> => {
    return apiClient.get<StandardResponse<any[]>>(`/responses/${id}/comments`);
  },

  addComment: async (id: string, comment: string, stageName?: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/responses/${id}/comments`, { content: comment, ...(stageName && { stageName }) });
  },

  updateComment: async (id: string, commentId: string, content: string): Promise<StandardResponse<any>> => {
    return apiClient.put<StandardResponse<any>>(`/responses/${id}/comments/${commentId}`, { content });
  },

  deleteComment: async (id: string, commentId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/responses/${id}/comments/${commentId}`);
  },

  // Response history and versioning
  getHistory: async (id: string): Promise<StandardResponse<any[]>> => {
    return apiClient.get<StandardResponse<any[]>>(`/responses/${id}/history`);
  },

  getVersion: async (id: string, version: number): Promise<StandardResponse<Response>> => {
    return apiClient.get<StandardResponse<Response>>(`/responses/${id}/versions/${version}`);
  },

  // Response analytics
  getAnalytics: async (params?: any): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/responses/analytics', { params });
  },

  // Draft locking
  requestRelease: async (id: string, reason?: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/draft-lock/request-release`, { reason });
  },

  grantRelease: async (id: string, requestId: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/draft-lock/grant-release/${requestId}`, {});
  },

  denyRelease: async (id: string, requestId: string, reason?: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/draft-lock/deny-release/${requestId}`, { reason });
  },

  releaseDraft: async (id: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/draft-lock/release`, {});
  },

  forceReleaseDraft: async (id: string, reason?: string): Promise<StandardResponse<Response>> => {
    return apiClient.post<StandardResponse<Response>>(`/responses/${id}/draft-lock/force-release`, { reason });
  },

  // Source correspondence management
  addCorrespondence: async (id: string, correspondenceId: string, closureType: 'full' | 'partial' = 'full', notes?: string): Promise<any> => {
    return apiClient.post<any>(`/responses/${id}/add-correspondence`, { correspondenceId, closureType, notes });
  },

  updateCorrespondenceClosure: async (id: string, correspondenceId: string, closureType: 'full' | 'partial'): Promise<any> => {
    return apiClient.patch<any>(`/responses/${id}/correspondence-closure`, { correspondenceId, closureType });
  }
};

// ===== EMAILS API =====
export const emails = {
  list: async (params?: EmailQueryParams): Promise<PaginatedResponse<Email>> => {
    return apiClient.get<PaginatedResponse<Email>>('/emails', { params });
  },

  get: async (id: string): Promise<StandardResponse<Email>> => {
    return apiClient.get<StandardResponse<Email>>(`/emails/${id}`);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/emails/${id}`);
  },

  markRead: async (emailId: string): Promise<StandardResponse<Email>> => {
    return apiClient.put<StandardResponse<Email>>(`/emails/${emailId}/mark-read`, {});
  },

  fetchEmails: async (): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/fetch-emails', {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  // Email preservation and lifecycle
  preserve: async (request: EmailPreservationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/preserve', request);
  },

  batchPreserve: async (request: EmailBatchOperationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/batch/preserve', request);
  },

  emergencyPreserve: async (emailIds: string[], reason: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/emergency-preserve', { emailIds, reason });
  },

  // Email to correspondence conversion
  convertToCorrespondence: async (emailId: string, projectId?: string, customFields?: Array<{ fieldId: string; name: string; type: string; value: any }>, sourceDocument?: { type: 'attachment'; attachmentId: string }, selectedAttachmentIds?: string[]): Promise<StandardResponse<Correspondence>> => {
    return apiClient.post<StandardResponse<Correspondence>>(`/emails/${emailId}/convert`, { projectId, customFields, sourceDocument, selectedAttachmentIds });
  },

  batchConvert: async (emailIds: string[], projectId?: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/batch-operations', { operation: 'classify', emailIds, projectId });
  },

  batchMarkRead: async (emailIds: string[]): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/batch-operations', { operation: 'markRead', emailIds });
  },

  batchDelete: async (emailIds: string[]): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/batch-operations', { operation: 'delete', emailIds });
  },

  syncAccount: async (accountId: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/emails/sync-account', { email: accountId });
  },

  syncAll: async (): Promise<StandardResponse<any>> => {
    
    // Use the existing fetch-emails endpoint
    return apiClient.post<StandardResponse<any>>('/emails/fetch-emails', {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },


  getSyncStatus: async (accountId?: string): Promise<StandardResponse<any>> => {
    const url = accountId ? `/emails/sync-status?email=${encodeURIComponent(accountId)}` : '/emails/sync-status';
    return apiClient.get<StandardResponse<any>>(url);
  },

  // Email search and filtering
  search: async (query: string, filters?: any): Promise<PaginatedResponse<Email>> => {
    return apiClient.get<PaginatedResponse<Email>>('/emails/search', { 
      params: { q: query, ...filters } 
    });
  },

  // Email analytics
  getAnalytics: async (params?: any): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/emails/analytics', { params });
  },

  getPreservationStats: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/emails/preservation-stats');
  }
};

// ===== ATTACHMENTS API =====
// Uploads are bounded by size, not by the API's request budget. Matches the
// server side: nginx and Express allow multipart requests up to 10 minutes.
const UPLOAD_TIMEOUT_MS = 20 * 60 * 1000;

export const attachments = {
  list: async (params?: AttachmentQueryParams): Promise<PaginatedResponse<Attachment>> => {
    return apiClient.get<PaginatedResponse<Attachment>>('/attachments', { params });
  },

  get: async (id: string): Promise<StandardResponse<Attachment>> => {
    return apiClient.get<StandardResponse<Attachment>>(`/attachments/${id}`);
  },

  upload: async (data: AttachmentUploadRequest): Promise<StandardResponse<AttachmentUploadResponse>> => {
    const formData = new FormData();


    data.files.forEach(file => {
      formData.append('files', file);
    });

    // Log FormData entries
    for (let pair of formData.entries()) {
    }

    if (data.emailId) formData.append('emailId', data.emailId);
    if (data.correspondenceId) formData.append('correspondenceId', data.correspondenceId);
    if (data.responseId) formData.append('responseId', data.responseId);
    if (data.attachmentType) formData.append('attachmentType', data.attachmentType);
    if (data.isPublic !== undefined) formData.append('isPublic', String(data.isPublic));
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));


    // Use instance directly with explicit config to ensure proper multipart handling
    try {
      const response = await apiClient.instance.post<StandardResponse<AttachmentUploadResponse>>(
        '/attachments/upload',
        formData,
        {
          // Let browser set Content-Type with multipart boundary automatically
          transformRequest: [(data) => data], // Don't transform FormData
          // A 60 MB file on an office uplink takes minutes. The client default
          // (30 s) aborted the transfer mid-stream; nginx logged it as 400 0B.
          timeout: UPLOAD_TIMEOUT_MS,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          onUploadProgress: data.onProgress
            ? (e: any) => {
                const total = e.total || e.event?.total;
                if (total) data.onProgress!(Math.min(100, Math.round((e.loaded * 100) / total)));
              }
            : undefined,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Upload error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.config?.headers
      });
      throw error;
    }
  },

  update: async (id: string, data: AttachmentUpdateRequest): Promise<StandardResponse<Attachment>> => {
    return apiClient.put<StandardResponse<Attachment>>(`/attachments/${id}`, data);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/attachments/${id}`);
  },

  // File operations
  download: async (id: string): Promise<Blob> => {
    const response = await apiClient.instance.get(`/attachments/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getDownloadUrl: async (id: string): Promise<StandardResponse<{ url: string; expiresAt: string }>> => {
    return apiClient.get<StandardResponse<{ url: string; expiresAt: string }>>(`/attachments/${id}/download-url`);
  },

  getPresignedUrl: async (id: string, options?: { inline?: boolean }): Promise<StandardResponse<{ presignedUrl: string; expiresAt: string; filename: string; contentType: string; size: number; sizeFormatted: string }>> => {
    const params = options?.inline ? '?inline=true' : '';
    return apiClient.get<StandardResponse<{ presignedUrl: string; expiresAt: string; filename: string; contentType: string; size: number; sizeFormatted: string }>>(`/attachments/${id}/presigned-url${params}`);
  },

  // Remove an attachment. Soft-deletes on the server and, for correspondence
  // attachments, unlinks it from the parent record.
  delete: async (id: string): Promise<StandardResponse<{ id: string; filename: string; deletedAt: string }>> => {
    return apiClient.delete<StandardResponse<{ id: string; filename: string; deletedAt: string }>>(`/attachments/${id}`);
  },

  // Attachment processing
  triggerProcessing: async (id: string): Promise<StandardResponse<Attachment>> => {
    return apiClient.post<StandardResponse<Attachment>>(`/attachments/${id}/process`, {});
  },

  getProcessingStatus: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/attachments/${id}/processing-status`);
  },

  triggerVirusScan: async (id: string): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>(`/attachments/${id}/virus-scan`, {});
  },

  // Attachment migration
  copyToCorrespondence: async (id: string, correspondenceId: string): Promise<StandardResponse<Attachment>> => {
    return apiClient.post<StandardResponse<Attachment>>(`/attachments/${id}/copy-to-correspondence`, { correspondenceId });
  },

  migrate: async (id: string, targetType: string): Promise<StandardResponse<Attachment>> => {
    return apiClient.post<StandardResponse<Attachment>>(`/attachments/${id}/migrate`, { targetType });
  },

  // Batch operations
  batchDelete: async (ids: string[]): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/attachments/batch/delete', { ids });
  },

  // NEW: Attachment versioning
  replaceAttachment: async (
    id: string,
    file: File,
    options?: {
      changeComment?: string;
      displayName?: string;
      description?: string;
      /** 0-100 as the bytes go up; see AttachmentUploadRequest.onProgress. */
      onProgress?: (percent: number) => void;
    }
  ): Promise<StandardResponse<Attachment>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.changeComment) {
      formData.append('changeComment', options.changeComment);
    }
    if (options?.displayName) {
      formData.append('displayName', options.displayName);
    }
    if (options?.description) {
      formData.append('description', options.description);
    }
    // Let axios automatically set Content-Type with boundary for FormData
    return apiClient.put<StandardResponse<Attachment>>(`/attachments/${id}/replace`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: UPLOAD_TIMEOUT_MS,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: options?.onProgress
        ? (e: any) => {
            const total = e.total || e.event?.total;
            if (total) options!.onProgress!(Math.min(100, Math.round((e.loaded * 100) / total)));
          }
        : undefined,
    } as any);
  },

  getVersionHistory: async (id: string): Promise<StandardResponse<{
    attachmentId: string;
    currentVersion: number;
    totalVersions: number;
    versions: Array<{
      versionNumber: number;
      filename: string;
      s3Key: string;
      s3Url: string;
      size: number;
      contentType: string;
      fileHash: string;
      updatedBy: { _id: string; name: string; email: string };
      updatedAt: string;
      changeComment: string;
    }>;
  }>> => {
    return apiClient.get<StandardResponse<any>>(`/attachments/${id}/versions`);
  },

  getVersion: async (id: string, versionNumber: number): Promise<StandardResponse<{
    versionNumber: number;
    filename: string;
    s3Key: string;
    s3Url: string;
    size: number;
    contentType: string;
    fileHash: string;
    updatedBy: { _id: string; name: string; email: string };
    updatedAt: string;
    changeComment: string;
  }>> => {
    return apiClient.get<StandardResponse<any>>(`/attachments/${id}/versions/${versionNumber}`);
  },

  downloadVersion: async (id: string, versionNumber: number): Promise<void> => {
    const url = `/api/attachments/${id}/versions/${versionNumber}/download`;
    window.open(url, '_blank');
  },

  batchUpdate: async (ids: string[], updates: Partial<AttachmentUpdateRequest>): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/attachments/batch/update', { ids, updates });
  },

  // Attachment analytics
  getStats: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/attachments/stats');
  },

  getStorageAnalytics: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/attachments/storage-analytics');
  }
};

// ===== NOTIFICATIONS API =====
export const notifications = {
  list: async (params?: NotificationQueryParams): Promise<PaginatedResponse<Notification>> => {
    return apiClient.get<PaginatedResponse<Notification>>('/notifications', { params });
  },

  get: async (id: string): Promise<StandardResponse<Notification>> => {
    return apiClient.get<StandardResponse<Notification>>(`/notifications/${id}`);
  },

  markAsRead: async (id: string): Promise<StandardResponse<Notification>> => {
    return apiClient.put<StandardResponse<Notification>>(`/notifications/${id}/mark-read`, {});
  },

  markAllAsRead: async (): Promise<StandardResponse<void>> => {
    return apiClient.put<StandardResponse<void>>('/notifications/mark-all-read', {});
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/notifications/${id}`);
  },

  // Batch operations
  batchMarkAsRead: async (ids: string[]): Promise<StandardResponse<void>> => {
    return apiClient.post<StandardResponse<void>>('/notifications/batch', { action: 'markRead', notificationIds: ids });
  },

  batchDelete: async (ids: string[]): Promise<StandardResponse<void>> => {
    return apiClient.post<StandardResponse<void>>('/notifications/batch', { action: 'delete', notificationIds: ids });
  },

  // Notification settings
  getSettings: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/notifications/settings');
  },

  updateSettings: async (settings: any): Promise<StandardResponse<any>> => {
    return apiClient.put<StandardResponse<any>>('/notifications/settings', settings);
  },

  // Notification summary
  getSummary: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/notifications/summary');
  }
};

// ===== EXCLUSIONS API =====
export const exclusions = {
  list: async (params?: ExclusionQueryParams): Promise<PaginatedResponse<Exclusion>> => {
    return apiClient.get<PaginatedResponse<Exclusion>>('/exclusions', { params });
  },

  // Organization-specific exclusions - FIXED: use 'id' parameter  
  listByOrganization: async (organizationId: string, params?: ExclusionQueryParams): Promise<PaginatedResponse<Exclusion>> => {
    return apiClient.get<PaginatedResponse<Exclusion>>(`/organizations/${organizationId}/exclusions`, { params });
  },

  get: async (exclusionId: string): Promise<StandardResponse<Exclusion>> => {
    return apiClient.get<StandardResponse<Exclusion>>(`/exclusions/${exclusionId}`);
  },

  create: async (exclusionData: ExclusionCreateRequest): Promise<StandardResponse<Exclusion>> => {
    return apiClient.post<StandardResponse<Exclusion>>('/exclusions', exclusionData);
  },

  update: async (exclusionId: string, exclusionData: ExclusionUpdateRequest): Promise<StandardResponse<Exclusion>> => {
    return apiClient.put<StandardResponse<Exclusion>>(`/exclusions/${exclusionId}`, exclusionData);
  },

  delete: async (exclusionId: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/exclusions/${exclusionId}`);
  },

  test: async (exclusionData: ExclusionTestRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/exclusions/test', exclusionData);
  },

  // Exclusion analytics (kept existing)
  getStats: async (exclusionId: string): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>(`/exclusions/${exclusionId}/stats`);
  },

  getAnalytics: async (params?: QueryParams): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/exclusions/analytics', { params });
  },

  // Exclusion management (kept existing)
  activate: async (exclusionId: string): Promise<StandardResponse<Exclusion>> => {
    return apiClient.post<StandardResponse<Exclusion>>(`/exclusions/${exclusionId}/activate`, {});
  },

  deactivate: async (exclusionId: string): Promise<StandardResponse<Exclusion>> => {
    return apiClient.post<StandardResponse<Exclusion>>(`/exclusions/${exclusionId}/deactivate`, {});
  },

  // Bulk operations (kept existing)
  bulkUpdate: async (updateData: BatchOperationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/exclusions/bulk-update', updateData);
  },

  bulkDelete: async (deleteData: BatchOperationRequest): Promise<StandardResponse<any>> => {
    return apiClient.post<StandardResponse<any>>('/exclusions/bulk-delete', deleteData);
  },

  // Search functionality (kept existing)
  search: async (query: string, filters?: any): Promise<PaginatedResponse<Exclusion>> => {
    return apiClient.get<PaginatedResponse<Exclusion>>('/exclusions/search', {
      params: { q: query, ...filters }
    });
  }
};

// ===== STATIC DOCUMENTS API =====
export const staticDocuments = {
  list: async (params?: StaticDocumentQueryParams): Promise<PaginatedResponse<StaticDocument>> => {
    return apiClient.get<PaginatedResponse<StaticDocument>>('/static-documents', { params });
  },

  get: async (id: string): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.get<StandardResponse<StaticDocument>>(`/static-documents/${id}`);
  },

  create: async (data: StaticDocumentCreateRequest): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>('/static-documents', data);
  },

  update: async (id: string, data: StaticDocumentUpdateRequest): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.put<StandardResponse<StaticDocument>>(`/static-documents/${id}`, data);
  },

  delete: async (id: string): Promise<StandardResponse<void>> => {
    return apiClient.delete<StandardResponse<void>>(`/static-documents/${id}`);
  },

  // Document lifecycle management
  publish: async (id: string): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>(`/static-documents/${id}/publish`, {});
  },

  unpublish: async (id: string): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>(`/static-documents/${id}/unpublish`, {});
  },

  archive: async (id: string): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>(`/static-documents/${id}/archive`, {});
  },

  restore: async (id: string): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>(`/static-documents/${id}/restore`, {});
  },

  // Version management
  getVersions: async (id: string): Promise<StandardResponse<any[]>> => {
    return apiClient.get<StandardResponse<any[]>>(`/static-documents/${id}/versions`);
  },

  getVersion: async (id: string, version: number): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.get<StandardResponse<StaticDocument>>(`/static-documents/${id}/versions/${version}`);
  },

  revertToVersion: async (id: string, version: number): Promise<StandardResponse<StaticDocument>> => {
    return apiClient.post<StandardResponse<StaticDocument>>(`/static-documents/${id}/revert/${version}`, {});
  },

  // Categories and tags
  getCategories: async (): Promise<StandardResponse<string[]>> => {
    return apiClient.get<StandardResponse<string[]>>('/static-documents/categories');
  },

  getTags: async (): Promise<StandardResponse<string[]>> => {
    return apiClient.get<StandardResponse<string[]>>('/static-documents/tags');
  },

  // Search functionality
  search: async (query: string, filters?: any): Promise<PaginatedResponse<StaticDocument>> => {
    return apiClient.get<PaginatedResponse<StaticDocument>>('/static-documents/search', {
      params: { q: query, ...filters }
    });
  },

  // File upload (multer → S3 → create record)
  upload: async (files: File[], metadata?: { description?: string; tags?: string[]; isPublic?: boolean; category?: string; projectId?: string; visibilityGroups?: string[] }): Promise<any> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (metadata?.description) formData.append('description', metadata.description);
    if (metadata?.tags) formData.append('tags', JSON.stringify(metadata.tags));
    if (metadata?.isPublic !== undefined) formData.append('isPublic', String(metadata.isPublic));
    if (metadata?.category) formData.append('category', metadata.category);
    if (metadata?.projectId) formData.append('projectId', metadata.projectId);
    if (metadata?.visibilityGroups) formData.append('visibilityGroups', JSON.stringify(metadata.visibilityGroups));
    return apiClient.post('/static-documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    } as any);
  },

  // Download (get presigned URL)
  download: async (id: string): Promise<{ downloadUrl: string; filename: string; expiresAt: string }> => {
    return apiClient.get(`/static-documents/${id}/download`);
  }
};

// ===== DASHBOARD API =====
export const dashboard = {
  getSummary: async (): Promise<StandardResponse<DashboardSummary>> => {
    return apiClient.get<StandardResponse<DashboardSummary>>('/dashboard');
  },

  /**
   * Actionable summary: responses awaiting THIS user's action, correspondences
   * awaiting a response in their projects, the classification queue, and what
   * changed since they last logged in. The page previously ignored this
   * endpoint and showed only raw totals.
   */
  getActionSummary: async (): Promise<StandardResponse<any>> => {
    return apiClient.get<StandardResponse<any>>('/dashboard/summary');
  },

  // OPTION 1: Direct period parameter (recommended)
  getMetrics: async (params?: DashboardQueryParams): Promise<StandardResponse<DashboardMetrics>> => {
    return apiClient.get<StandardResponse<DashboardMetrics>>('/dashboard/metrics', { params });
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Generic file upload helper
 */
export const uploadFile = async (
  endpoint: string, 
  files: File | File[], 
  additionalData?: Record<string, any>,
  onProgress?: (progress: number) => void
): Promise<StandardResponse<any>> => {
  const formData = new FormData();
  
  if (Array.isArray(files)) {
    files.forEach(file => formData.append('files', file));
  } else {
    formData.append('file', files);
  }
  
  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });
  }

  return apiClient.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for large file uploads
  });
};

/**
 * Generic download helper
 */
export const downloadFile = async (
  endpoint: string, 
  filename?: string,
  params?: Record<string, any>
): Promise<void> => {
  try {
    const response = await apiClient.instance.get(endpoint, {
      responseType: 'blob',
      params
    });

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

/**
 * Batch operation helper
 */
export const batchOperation = async <T>(
  items: T[],
  operation: (item: T) => Promise<any>,
  batchSize: number = 10,
  onProgress?: (completed: number, total: number) => void
): Promise<any[]> => {
  const results: any[] = [];
  const total = items.length;
  
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchPromises = batch.map(operation);
    
    try {
      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults);
      
      if (onProgress) {
        onProgress(Math.min(i + batchSize, total), total);
      }
    } catch (error) {
      console.error(`Batch ${i / batchSize + 1} failed:`, error);
      throw error;
    }
  }
  
  return results;
};

/**
 * Cache management utilities
 */
export const cache = {
  get: <T>(key: string): T | null => cacheManager.get<T>(key),
  set: <T>(key: string, data: T, ttl?: number): void => cacheManager.set(key, data, ttl),
  delete: (key: string): void => cacheManager.delete(key),
  clear: (pattern?: string): void => cacheManager.clear(pattern),
  has: (key: string): boolean => cacheManager.has(key)
};

/**
 * Request queue for handling offline scenarios
 */
export const requestQueue = {
  queue: [] as Array<{ method: string; url: string; data?: any; resolve: Function; reject: Function }>,
  
  add: <T>(method: string, url: string, data?: any): Promise<T> => {
    return new Promise((resolve, reject) => {
      requestQueue.queue.push({ method, url, data, resolve, reject });
    });
  },
  
  process: async (): Promise<void> => {
    const queueCopy = [...requestQueue.queue];
    requestQueue.queue = [];
    
    for (const request of queueCopy) {
      try {
        let result;
        switch (request.method.toLowerCase()) {
          case 'get':
            result = await apiClient.get(request.url);
            break;
          case 'post':
            result = await apiClient.post(request.url, request.data);
            break;
          case 'put':
            result = await apiClient.put(request.url, request.data);
            break;
          case 'delete':
            result = await apiClient.delete(request.url);
            break;
          default:
            throw new Error(`Unsupported method: ${request.method}`);
        }
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      }
    }
  },
  
  clear: (): void => {
    requestQueue.queue = [];
  },
  
  size: (): number => requestQueue.queue.length
};

// ===== WEBSOCKET CONNECTION =====
let wsConnection: WebSocket | null = null;
let wsReconnectTimer: NodeJS.Timeout | null = null;
let wsReconnectAttempts = 0;
const maxReconnectAttempts = 5;

export const websocket = {
  connect: (onMessage?: (data: any) => void, onError?: (error: Event) => void): void => {
    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.warn('No auth token available for WebSocket connection');
      return;
    }

    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3000/ws';
    try {
      wsConnection = new WebSocket(`${wsUrl}?token=${token}`);

      wsConnection.onopen = () => {
        wsReconnectAttempts = 0;
        if (wsReconnectTimer) {
          clearTimeout(wsReconnectTimer);
          wsReconnectTimer = null;
        }
      };

      wsConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onMessage) {
            onMessage(data);
          }
          
          // Handle system-level messages
          if (data.type === 'notification') {
            cache.delete('notifications');
          } else if (data.type === 'workflow_update') {
            cache.clear('responses');
            cache.clear('workflows');
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      wsConnection.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (onError) {
          onError(error);
        }
      };

      wsConnection.onclose = () => {
        wsConnection = null;
        
        // Attempt to reconnect with exponential backoff
        if (wsReconnectAttempts < maxReconnectAttempts) {
          wsReconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts), 30000);
          
          wsReconnectTimer = setTimeout(() => {
            websocket.connect(onMessage, onError);
          }, delay);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      if (onError) {
        onError(error as Event);
      }
    }
  },

  // ... rest of websocket methods remain the same
  disconnect: (): void => {
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer);
      wsReconnectTimer = null;
    }
    
    if (wsConnection) {
      wsConnection.close();
      wsConnection = null;
    }
    
    wsReconnectAttempts = 0;
  },

  send: (data: any): void => {
    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      try {
        wsConnection.send(JSON.stringify(data));
      } catch (error) {
        console.error('Failed to send WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  },

  isConnected: (): boolean => {
    return wsConnection !== null && wsConnection.readyState === WebSocket.OPEN;
  }
};

// ===== ADMIN API =====
export const admin = {
  // Get storage metrics
  getStorageMetrics: async (): Promise<StandardResponse<{
    total: number;
    totalMB: string;
    totalGB: string;
    breakdown: {
      emails: { totalSize: number; count: number };
      attachments: { totalSize: number; count: number };
      correspondences: { totalSize: number; count: number };
      documents: { totalSize: number; count: number };
    };
  }>> => {
    return apiClient.get<StandardResponse<any>>('/admin/metrics/storage');
  },

  // Get activity logs
  getActivity: async (params?: {
    limit?: number;
    offset?: number;
    type?: string;
    userId?: string;
    entityType?: string;
  }): Promise<PaginatedResponse<any>> => {
    const queryString = new URLSearchParams(params as any).toString();
    return apiClient.get<PaginatedResponse<any>>(`/admin/activity${queryString ? `?${queryString}` : ''}`);
  },

  // System backup
  createBackup: async (): Promise<StandardResponse<{
    backupName: string;
    backupSize: number;
    backupSizeMB: string;
    backupPath: string;
    timestamp: string;
  }>> => {
    return apiClient.post<StandardResponse<any>>('/admin/system/backup', {});
  },

  // Clear cache
  clearCache: async (): Promise<StandardResponse<{ message: string; timestamp: string }>> => {
    return apiClient.delete<StandardResponse<any>>('/admin/system/cache');
  },

  // Sync all emails
  syncAllEmails: async (): Promise<StandardResponse<{
    accountCount: number;
    timestamp: string;
  }>> => {
    return apiClient.post<StandardResponse<any>>('/admin/emails/sync-all', {});
  },

  // Toggle maintenance mode
  toggleMaintenance: async (enabled: boolean): Promise<StandardResponse<{
    maintenanceMode: boolean;
    timestamp: string;
  }>> => {
    return apiClient.post<StandardResponse<any>>('/admin/system/maintenance', { enabled });
  },

  // Restart services
  restartServices: async (): Promise<StandardResponse<{
    timestamp: string;
    note: string;
  }>> => {
    return apiClient.post<StandardResponse<any>>('/admin/system/restart', {});
  },

  // Org-scoped admin endpoints (for admin role users)
  getOrgMetrics: async (): Promise<StandardResponse<{
    organization: { name: string; plan: string; isActive: boolean; isAIEnabled: boolean };
    counts: { users: number; emails: number; correspondences: number; responses: number; projects: number; attachments: number };
    storage: { totalBytes: number; totalMB: string };
  }>> => {
    return apiClient.get<StandardResponse<any>>('/admin/org/metrics');
  },

  getOrgActivity: async (params?: {
    limit?: number;
    offset?: number;
    action?: string;
    resourceType?: string;
    userId?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<any>> => {
    const queryString = params ? new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
    ).toString() : '';
    return apiClient.get<PaginatedResponse<any>>(`/admin/org/activity${queryString ? `?${queryString}` : ''}`);
  }
};

// ===== ORGANIZATION CUSTOMIZATION API =====
const organizationCustomization = {
  getFields: async (): Promise<any> => {
    return apiClient.get<any>('/organization-customization/fields');
  },
  getGridConfig: async (gridType: string): Promise<any> => {
    return apiClient.get<any>(`/organization-customization/grid/${gridType}`);
  },
  getCustomization: async (): Promise<any> => {
    return apiClient.get<any>('/organization-customization');
  },
  createCustomization: async (data: any): Promise<any> => {
    return apiClient.post<any>('/organization-customization', data);
  },
  updateCustomization: async (data: any): Promise<any> => {
    return apiClient.put<any>('/organization-customization', data);
  },
  addField: async (fieldType: string, fieldData: any): Promise<any> => {
    return apiClient.post<any>(`/organization-customization/fields/${fieldType}`, fieldData);
  },
  updateField: async (fieldType: string, fieldId: string, fieldData: any): Promise<any> => {
    return apiClient.put<any>(`/organization-customization/fields/${fieldType}/${fieldId}`, fieldData);
  },
  deleteField: async (fieldType: string, fieldId: string): Promise<any> => {
    return apiClient.delete<any>(`/organization-customization/fields/${fieldType}/${fieldId}`);
  },
  updateGridConfig: async (gridType: string, config: any): Promise<any> => {
    return apiClient.put<any>(`/organization-customization/grid/${gridType}`, config);
  },
  syncTemplate: async (): Promise<any> => {
    return apiClient.post<any>('/organization-customization/sync-template', {});
  }
};

// ===== INDUSTRY TEMPLATES API =====
const industryTemplates = {
  list: async (params?: { industry?: string; status?: string; search?: string }): Promise<any> => {
    const queryString = params ? new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
    ).toString() : '';
    return apiClient.get<any>(`/industry-templates${queryString ? `?${queryString}` : ''}`);
  },
  getIndustries: async (): Promise<any> => {
    return apiClient.get<any>('/industry-templates/industries');
  },
  getById: async (id: string): Promise<any> => {
    return apiClient.get<any>(`/industry-templates/${id}`);
  }
};

// ===== ORGANIZATION SETTINGS (storage, AI, email providers) =====
export const orgSettings = {
  // ----- Cloud Storage -----
  listStorageProviders: async (): Promise<any> =>
    apiClient.get<any>('/organization-settings/storage/providers'),

  getStorageProviderInstructions: async (provider: string): Promise<any> =>
    apiClient.get<any>(`/organization-settings/storage/providers/${provider}/instructions`),

  getStorage: async (): Promise<any> =>
    apiClient.get<any>('/organization-settings/storage'),

  updateStorage: async (data: {
    useOwnStorage?: boolean;
    provider?: string | null;
    config?: Record<string, any>;
    credentials?: Record<string, string>;
    fallbackToDefault?: boolean;
  }): Promise<any> => apiClient.put<any>('/organization-settings/storage', data),

  validateStorage: async (): Promise<any> =>
    apiClient.post<any>('/organization-settings/storage/validate', {}),

  resetStorage: async (): Promise<any> =>
    apiClient.delete<any>('/organization-settings/storage'),

  // ----- AI Providers -----
  listAIProviders: async (): Promise<any> =>
    apiClient.get<any>('/organization-settings/ai/providers'),

  getAI: async (): Promise<any> =>
    apiClient.get<any>('/organization-settings/ai'),

  updateAI: async (data: {
    enabled?: boolean;
    primaryProvider?: string | null;
    providers?: Record<string, any>;
    features?: Record<string, boolean>;
  }): Promise<any> => apiClient.put<any>('/organization-settings/ai', data),

  validateAIKey: async (provider: string, apiKey: string): Promise<any> =>
    apiClient.post<any>(`/organization-settings/ai/validate/${provider}`, { apiKey }),

  resetAI: async (): Promise<any> =>
    apiClient.delete<any>('/organization-settings/ai'),

  getAIUsage: async (): Promise<any> =>
    apiClient.get<any>('/organization-settings/ai/usage'),

  // ----- Microsoft 365 (mounted on /organizations/:id/email-providers/microsoft365) -----
  getMicrosoft365: async (orgId: string): Promise<any> =>
    apiClient.get<any>(`/organizations/${orgId}/email-providers`),

  updateMicrosoft365: async (orgId: string, data: {
    enabled?: boolean;
    authType?: 'delegated' | 'application_permissions';
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    allowedDomains?: string[];
  }): Promise<any> => apiClient.put<any>(`/organizations/${orgId}/email-providers/microsoft365`, data),

  validateMicrosoft365: async (orgId: string): Promise<any> =>
    apiClient.post<any>(`/organizations/${orgId}/email-providers/microsoft365/validate`, {}),

  // Admin-provisioned M365 mailbox assignments
  listProvisionedMailboxes: async (orgId: string): Promise<any> =>
    apiClient.get<any>(`/organizations/${orgId}/email-providers/microsoft365/mailboxes`),

  addProvisionedMailbox: async (orgId: string, data: { email: string; displayName?: string; assignedToUserId: string }): Promise<any> =>
    apiClient.post<any>(`/organizations/${orgId}/email-providers/microsoft365/mailboxes`, data),

  removeProvisionedMailbox: async (orgId: string, assignmentId: string): Promise<any> =>
    apiClient.delete<any>(`/organizations/${orgId}/email-providers/microsoft365/mailboxes/${assignmentId}`),

  // Set which projects a provisioned mailbox's mail may be classified into.
  updateMailboxProjects: async (orgId: string, assignmentId: string, assignedProjects: string[]): Promise<any> =>
    apiClient.patch<any>(`/organizations/${orgId}/email-providers/microsoft365/mailboxes/${assignmentId}/projects`, { assignedProjects }),

  // Projects a given user can access — populates the mailbox project picker.
  getUserAccessibleProjects: async (orgId: string, userId: string): Promise<any> =>
    apiClient.get<any>(`/organizations/${orgId}/users/${userId}/accessible-projects`)
};

// ===== EXPORT ALL API MODULES =====
export default {
  // Core axios instance methods
  get: apiClient.get.bind(apiClient),
  post: apiClient.post.bind(apiClient),
  put: apiClient.put.bind(apiClient),
  delete: apiClient.delete.bind(apiClient),
  // Core APIs
  auth,
  organizations,
  users,
  groups,
  workflows,
  projects,
  correspondences,
  responses,
  emails,
  attachments,
  notifications,
  exclusions,
  staticDocuments,
  dashboard,
  admin,
  organizationCustomization,
  industryTemplates,
  orgSettings,

  // Real-time
  websocket,
  
  // Utility functions
  uploadFile,
  downloadFile,
  batchOperation,
  cache,
  requestQueue,
  
  // Direct access to axios instance
  apiClient: apiClient.instance,

  // Configuration
  config: API_CONFIG
};

// ===== GLOBAL EVENT HANDLERS =====
if (typeof window !== 'undefined') {
  // Handle connection status changes
  window.addEventListener('online', () => {
    requestQueue.process();
  });

  // Auto-connect WebSocket on authentication
  const checkAuthAndConnectWS = () => {
    // Don't connect WebSocket on login page
    if (window.location.pathname === '/login') {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (token && !websocket.isConnected()) {
      websocket.connect(
        (data) => {
          // Emit custom events for components to listen to
          window.dispatchEvent(new CustomEvent('realtimeUpdate', {
            detail: data
          }));
        },
        (error) => {
          console.warn('WebSocket connection error:', error);
        }
      );
    }
  };

  // Check auth status on module load
  checkAuthAndConnectWS();

  // Listen for auth state changes
  window.addEventListener('authStateChanged', checkAuthAndConnectWS);

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    websocket.disconnect();
  });
}

// ===== TYPE EXPORTS FOR CONSUMERS =====
export type {
  // Re-export commonly used types
  User,
  Organization,
  Group,
  Project,
  Workflow,
  Correspondence,
  Response,
  Attachment,
  Email,
  StandardResponse,
  PaginatedResponse,
  ApiErrorResponse,
  QueryParams
};

// ===== VERSION INFO =====
export const API_VERSION = '6.0.0';
export const LAST_UPDATED = '2025-01-17';

// Log initialization